import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Avatar,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
  Checkbox,
  ListItemText,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Stack,
  IconButton,
  useMediaQuery,
  Card,
  CardContent,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { EmailDetailsModal } from './EmailDetailsModal';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailApi } from '@/api/email';

interface EmailsListProps {
  ownerId?: string;
  streamId?: string;
  prospectId?: string;
  showStreamColumn?: boolean;
  showOwnerColumn?: boolean;
}

const EMAIL_TYPES = [
  'DISCOVERY_MEETING','FOLLOW_UP','INTRO','DEMO_INVITE','MEETING_CONFIRMATION','REMINDER','NEWSLETTER','ONBOARDING','TRIAL_ENDING','REENGAGEMENT','GENERAL','CALL','MORE_INFORMATION_EMAIL','CONTACT_EMAIL'
];

export const EmailsList: React.FC<EmailsListProps> = ({
  ownerId,
  streamId,
  prospectId,
  showStreamColumn = false,
  showOwnerColumn = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [types, setTypes] = useState<string[]>(['DISCOVERY_MEETING']);
  const [search, setSearch] = useState<string>('');
  const [sortField, setSortField] = useState<string>('send_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedEmail, setSelectedEmail] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; email: any } | null>(null);
  const [emailToDelete, setEmailToDelete] = useState<any | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (emailUuid: string) => emailApi.delete(emailUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['emails', { ownerId, streamId, prospectId, types, search, sortField, sortDirection }],
      });
      setEmailToDelete(null);
    },
    onError: (error: unknown) => {
      console.error('Failed to delete email:', error);
    },
  });

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['emails', { ownerId, streamId, prospectId, types, search, sortField, sortDirection }],
    queryFn: ({ pageParam }) =>
      emailApi.list({
        owner: ownerId,
        stream: streamId,
        prospect: prospectId,
        types,
        search: search || undefined,
        sort_field: sortField,
        sort_direction: sortDirection,
        page_token: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_page_token || undefined,
  });

  const items = (data?.pages || []).flatMap((p) => p.items) ?? [];
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, { root: null, rootMargin: '400px', threshold: 0 });

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  const showProspectColumn = !prospectId;
  const columns =
    (showProspectColumn ? 1 : 0) + 6 + (showStreamColumn ? 1 : 0) + (showOwnerColumn ? 1 : 0) + 1;
  const gridCols = [
    ...(showProspectColumn ? ['2fr'] : []),
    '1.6fr',
    '1.8fr',
    '2fr',
    '2fr',
    '1fr',
    '1.6fr',
    ...(showStreamColumn ? ['1.6fr'] : []),
    ...(showOwnerColumn ? ['1.6fr'] : []),
    'auto',
  ].join(' ');

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, email: any) => {
    e.stopPropagation();
    setMenuAnchor({ el: e.currentTarget, email });
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const handleDeleteClick = () => {
    if (menuAnchor?.email) {
      setEmailToDelete(menuAnchor.email);
      handleMenuClose();
    }
  };

  const handleConfirmDelete = () => {
    const uuid = emailToDelete?.key || emailToDelete?.uuid;
    if (uuid) {
      deleteMutation.mutate(uuid);
    }
  };

  const handleCancelDelete = () => setEmailToDelete(null);

  return (
    <Box sx={{ p: 0 }}>
      {/* Filter Bar - match campaigns style */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 2 },
        p: 2,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        mb: 2,
      }}>
        {/* Search */}
        <TextField
          placeholder="Search emails..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 240 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />

        {/* Active Filters Chips */}
        <Stack direction="row" spacing={0} sx={{ flex: { xs: 'none', sm: 1 }, minWidth: 0, flexWrap: 'wrap', '& .MuiChip-root': { mb: { xs: 1, sm: 0 } } }}>
          {types.length > 0 && (
            <Chip
              label={`Types: ${types.length}`}
              size="small"
              onDelete={() => setTypes([])}
              color="primary"
              variant="outlined"
              sx={{ mr: 1 }}
            />
          )}
          {search && (
            <Chip
              label={`Search: "${search}"`}
              size="small"
              onDelete={() => setSearch('')}
              color="secondary"
              variant="outlined"
              sx={{ mr: 1 }}
            />
          )}
          <Chip
            label={`Sort: ${sortField} ${sortDirection === 'desc' ? '↓' : '↑'}`}
            size="small"
            onDelete={() => { setSortField('send_date'); setSortDirection('desc'); }}
            color="default"
            variant="outlined"
            sx={{ mr: 1 }}
          />
        </Stack>

        {/* Type Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 }, width: { xs: '100%', sm: 'auto' } }}>
          <InputLabel id="email-types-label">Type</InputLabel>
          <Select
            labelId="email-types-label"
            multiple
            value={types}
            onChange={(e) => setTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]))}
            input={<OutlinedInput label="Type" />}
            startAdornment={<FilterIcon fontSize="small" sx={{ mr: 1 }} />}
            renderValue={(selected) => (selected as string[]).join(', ')}
            sx={{ '& .MuiSelect-select': { padding: '8px 12px', fontSize: '0.875rem', display: 'flex', alignItems: 'center' }, borderRadius: 2 }}
          >
            {EMAIL_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                <Checkbox checked={types.indexOf(t) > -1} />
                <ListItemText primary={t} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sort controls - keep in one row on mobile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <FormControl size="small" sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 150 } }}>
            <InputLabel id="sort-field-label">Sort By</InputLabel>
            <Select labelId="sort-field-label" value={sortField} label="Sort By" onChange={(e) => setSortField(e.target.value)} sx={{ '& .MuiSelect-select': { padding: '8px 12px' }, borderRadius: 2 }}>
              <MenuItem value="send_date">Send Date</MenuItem>
              <MenuItem value="type">Type</MenuItem>
            </Select>
          </FormControl>
          <IconButton
            size="small"
            onClick={() => setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 1,
              minWidth: 40,
              minHeight: 40,
              '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' }
            }}
          >
            {sortDirection === 'desc' ? <ArrowDownIcon fontSize="small" /> : <ArrowUpIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      {/* Status rows for non-mobile are rendered within the grid below to keep headers fixed */}

      {/* Desktop/tablet list */}
      {!isMobile && (
        <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, gap: 1, alignItems: 'center' }}>
          {/* Header */}
          {showProspectColumn && <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Prospect</Typography>}
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>To</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>From</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Subject</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Preview</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Type</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Send Date</Typography>
          {showStreamColumn && <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Experiment</Typography>}
          {showOwnerColumn && <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Operator</Typography>}
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }} />

          {/* Loading */}
          {isLoading && (
            <Box sx={{ gridColumn: `1 / span ${columns}`, display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error */}
          {!isLoading && isError && (
            <Box sx={{ gridColumn: `1 / span ${columns}` }}>
              <Alert severity="error">Failed to load emails.</Alert>
            </Box>
          )}

          {/* Empty */}
          {!isLoading && !isError && items.length === 0 && (
            <Box sx={{ gridColumn: `1 / span ${columns}`, textAlign: 'center', py: 8 }}>
              <Typography color="text.secondary">No emails found</Typography>
            </Box>
          )}

          {/* Rows */}
          {!isLoading && !isError && items.map((e) => (
            <React.Fragment key={e.key || e.uuid}>
              {showProspectColumn && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => { setSelectedEmail(e); setIsModalOpen(true); }}>
                  <Avatar src={e.prospect?.profile_image || undefined} sx={{ width: 24, height: 24 }} />
                  <Typography variant="body2">{e.prospect?.full_name || (e.prospect ? `${e.prospect.first_name || ''} ${e.prospect.last_name || ''}`.trim() : '-') || '-'}</Typography>
                </Box>
              )}
              <Typography variant="body2" sx={{ cursor: 'pointer' }} onClick={() => { setSelectedEmail(e); setIsModalOpen(true); }}>{e.to}</Typography>
              <Typography variant="body2" sx={{ cursor: 'pointer' }} onClick={() => { setSelectedEmail(e); setIsModalOpen(true); }}>{[e.from_name, e.from_email].filter(Boolean).join(' <') + (e.from_email ? '>' : '')}</Typography>
              <Typography variant="body2" noWrap title={e.subject || ''} sx={{ cursor: 'pointer' }} onClick={() => { setSelectedEmail(e); setIsModalOpen(true); }}>{e.subject}</Typography>
              {/* One-line sanitized preview */}
              <Typography variant="body2" noWrap title={e.message || ''} sx={{ color: 'text.secondary', cursor: 'pointer' }} onClick={() => { setSelectedEmail(e); setIsModalOpen(true); }}>
                {(() => {
                  const tmp = document.createElement('div');
                  tmp.innerHTML = e.message || '';
                  const text = tmp.textContent || tmp.innerText || '';
                  return text.replace(/\s+/g, ' ').trim();
                })()}
              </Typography>
              {/* Type tag */}
              <Chip size="small" label={e.type || '-'} color="default" variant="outlined" />
              <Typography variant="body2" sx={{ cursor: 'pointer' }} onClick={() => { setSelectedEmail(e); setIsModalOpen(true); }}>{e.send_date ? new Date(e.send_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</Typography>
              {showStreamColumn && (
                <Box onClick={(ev) => { ev.stopPropagation(); if (e.stream?.key || e.stream?.uuid) navigate(`/campaigns/${e.stream.key || e.stream.uuid}`); }}>
                  <Chip
                    size="small"
                    label={(e.stream && e.stream.name) || '-'}
                    variant="outlined"
                    clickable={!!(e.stream?.key || e.stream?.uuid)}
                    sx={{
                      cursor: (e.stream?.key || e.stream?.uuid) ? 'pointer' : 'default',
                      color: 'text.primary',
                      borderColor: 'divider',
                      maxWidth: 180,
                      '& .MuiChip-label': {
                        maxWidth: 160,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                      '&:hover': { bgcolor: (e.stream?.key || e.stream?.uuid) ? 'action.hover' : 'transparent' },
                    }}
                  />
                </Box>
              )}
              {showOwnerColumn && (
                <Typography variant="body2" sx={{ cursor: 'pointer' }} onClick={() => { setSelectedEmail(e); setIsModalOpen(true); }}>
                  {e.owner?.name || e.owner?.email || '-'}
                </Typography>
              )}
              <Box onClick={(ev) => ev.stopPropagation()}>
                <IconButton size="small" onClick={(ev) => handleMenuOpen(ev, e)}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </React.Fragment>
          ))}
          {/* Infinite scroll sentinel and states */}
          {(!isLoading && !isError && items.length > 0 && (isFetchingNextPage || hasNextPage)) && (
            <Box sx={{ gridColumn: `1 / span ${columns}`, display: 'flex', justifyContent: 'center', py: 2 }}>
              {isFetchingNextPage ? (
                <CircularProgress size={24} />
              ) : (
                <Box ref={sentinelRef} sx={{ height: 1 }} />
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Mobile list */}
      {isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((e) => (
            <Card key={e.key || e.uuid} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', cursor: 'pointer' }} onClick={() => { setSelectedEmail(e); setIsModalOpen(true); }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, position: 'relative' }}>
                {/* Header row with prospect (when not filtered by prospect) and menu */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: showProspectColumn ? 1 : 0 }}>
                  {showProspectColumn && (
                    <>
                      <Avatar src={e.prospect?.profile_image || undefined} sx={{ width: 32, height: 32 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{e.prospect?.full_name || (e.prospect ? `${e.prospect.first_name || ''} ${e.prospect.last_name || ''}`.trim() : '-') || '-'}</Typography>
                      </Box>
                    </>
                  )}
                  {!showProspectColumn && <Box sx={{ flex: 1 }} />}
                  <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); handleMenuOpen(ev, e); }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
                {/* Type - its own row */}
                <Box sx={{ mb: 0.5 }}>
                  <Chip size="small" label={e.type || '-'} variant="outlined" />
                </Box>
                {/* To label */}
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, mb: 0.5 }}>to: {e.to || '-'}</Typography>
                
                {/* Subject */}
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, lineHeight: 1.3 }} title={e.subject || ''}>
                  {e.subject || '-'}
                </Typography>
                
                {/* Message preview */}
                <Box sx={{ color: 'text.secondary', mb: 1, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={e.message || ''}>
                  <Typography variant="body2">
                    {(() => {
                      const tmp = document.createElement('div');
                      tmp.innerHTML = e.message || '';
                      const text = tmp.textContent || tmp.innerText || '';
                      return text.replace(/\s+/g, ' ').trim();
                    })()}
                  </Typography>
                </Box>
                
                {/* Stream and Operator when applicable */}
                {(showStreamColumn || showOwnerColumn) && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                    {showStreamColumn && (
                      <Box onClick={(ev) => { ev.stopPropagation(); if (e.stream?.key || e.stream?.uuid) navigate(`/campaigns/${e.stream.key || e.stream.uuid}`); }}>
                        <Chip
                          size="small"
                          label={(e.stream && e.stream.name) || '-'}
                          variant="outlined"
                          clickable={!!(e.stream?.key || e.stream?.uuid)}
                          sx={{
                            cursor: (e.stream?.key || e.stream?.uuid) ? 'pointer' : 'default',
                            color: 'text.primary',
                            borderColor: 'divider',
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                            '&:hover': { bgcolor: (e.stream?.key || e.stream?.uuid) ? 'action.hover' : 'transparent' },
                          }}
                        />
                      </Box>
                    )}
                    {showOwnerColumn && (
                      <Chip size="small" label={e.owner?.name || e.owner?.email || '-'} variant="outlined" sx={{ borderColor: 'divider' }} />
                    )}
                  </Box>
                )}
                
                {/* Footer with date and stream */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {e.send_date ? new Date(e.send_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
          {/* Infinite scroll sentinel and states for mobile */}
          {(!isLoading && !isError && items.length > 0 && (isFetchingNextPage || hasNextPage)) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              {isFetchingNextPage ? (
                <CircularProgress size={24} />
              ) : (
                <Box ref={sentinelRef} sx={{ height: 1 }} />
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Row actions menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(emailToDelete)} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>Delete email</DialogTitle>
        <DialogContent dividers>
          {deleteMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to delete email. Please try again.
            </Alert>
          )}
          <Typography variant="body1">
            Are you sure you want to delete this email? This will remove the email record and all
            its traces. This action cannot be undone.
          </Typography>
          {emailToDelete && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              To: {emailToDelete.to} | Subject: {emailToDelete.subject || '(no subject)'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={deleteMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Modal */}
      <EmailDetailsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        email={selectedEmail}
      />
    </Box>
  );
};

export default EmailsList;


